import {
	endpointsMap,
	ICreateNewsletterProfileOptions,
	ICreateNewsletterProfileResponse,
	ISearchNewsletterProfileOptions,
	ISearchNewsletterProfileResponse,
	ISubscribeToNewsletterOptions,
	ISubscribeToNewsletterResponse,
	IUnsubscribeToNewsletterOptions,
	IUnsubscribeToNewsletterResponse,
	IUnsubscribeFromNewsletterByUidOptions,
	IUnsubscribeFromNewsletterByUidResponse,
	IUpdateNewsletterProfileOptions,
	IUpdateNewsletterProfileResponse,
	IAddNewsletterPreferencesOptions,
	IAddNewsletterPreferencesResponse
} from './ICustomShopperNewsletter';

import {ICustomApiConfig} from '../ICustomApiConfig';
import AbstractCustomShopperAPI from '../AbstractCustomShopperAPI';

/**
 * Represents CustomShopperNewsletter
 */
class CustomShopperNewsletter extends AbstractCustomShopperAPI {
	/**
	 * @constructor
	 * @param {ICustomApiConfig} config - API Config
	 */
	constructor(config: ICustomApiConfig) {
		super(config);
	}

	/**
	 * Gets newsletter profile
	 * @param {ISearchNewsletterProfileOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {ISearchNewsletterProfileResponse} - Customer's newsletter profile
	 */
	public async searchNewsletterProfile(
		fetchOptions: ISearchNewsletterProfileOptions,
		throwOnError?: boolean
	): Promise<ISearchNewsletterProfileResponse> {
		const response: ISearchNewsletterProfileResponse = await this.callCustomEndpoint<ISearchNewsletterProfileResponse>(
			fetchOptions,
			endpointsMap.SEARCH_PROFILE
		);

		if (throwOnError && !response.newsletterProfile) {
			throw new Error('No profile returned.');
		}

		return response;
	}

	/**
	 * Creates newsletter profile
	 * @param {ICreateNewsletterProfileOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {ICreateNewsletterProfileResponse} - Status of the request
	 */
	public async createNewsletterProfile(
		fetchOptions: ICreateNewsletterProfileOptions,
		throwOnError?: boolean
	): Promise<ICreateNewsletterProfileResponse> {
		const response: ICreateNewsletterProfileResponse = await this.callCustomEndpoint<ICreateNewsletterProfileResponse>(
			fetchOptions,
			endpointsMap.CREATE_PROFILE
		);

		if (throwOnError && !response.subscriptionCode) {
			throw new Error('Create newsletter profile failed.');
		}

		return response;
	}

	/**
	 * Updates newsletter profile
	 * @param {IUpdateNewsletterProfileOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {IUpdateNewsletterProfileResponse} - Status of the request
	 */
	public async updateNewsletterProfile(
		fetchOptions: IUpdateNewsletterProfileOptions,
		throwOnError?: boolean
	): Promise<IUpdateNewsletterProfileResponse> {
		const response: IUpdateNewsletterProfileResponse = await this.callCustomEndpoint<IUpdateNewsletterProfileResponse>(
			fetchOptions,
			endpointsMap.UPDATE_PROFILE
		);

		if (throwOnError && !response.subscriptionCode) {
			throw new Error('Update newsletter profile failed.');
		}

		return response;
	}

	/**
	* Add customer newsletter preferences
	* @param {IAddNewsletterPreferencesOptions} fetchOptions - options to pass to API Call
	* @param {boolean} throwOnError - throw error if api returns error
	*
	* @returns {IBaseResponse}
	*/
	public async addNewsletterPreferences(
		fetchOptions: IAddNewsletterPreferencesOptions,
		throwOnError?: boolean
	): Promise<IAddNewsletterPreferencesResponse> {
		const response: IAddNewsletterPreferencesResponse = await this.callCustomEndpoint(
			fetchOptions,
			endpointsMap.ADD_PREFERENCES
		);
		const hasError = /error/.test(response?.type);

		if (throwOnError || hasError) {
			throw new Error('Adding preferences failed.');
		}

		return response;
	}

	/**
	 * Subscribes customer to newsletter
	 * @param {ISubscribeToNewsletterOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {ISubscribeToNewsletterResponse} - Status of the request
	 */
	public async subscribeToNewsletter(
		fetchOptions: ISubscribeToNewsletterOptions,
		throwOnError?: boolean
	): Promise<ISubscribeToNewsletterResponse> {
		const response: ISubscribeToNewsletterResponse = await this.callCustomEndpoint<ISubscribeToNewsletterResponse>(
			fetchOptions,
			endpointsMap.SUBSCRIBE
		);

		if (throwOnError && !response.subscriptionCode) {
			throw new Error('Subscription failed.');
		}

		return response;
	}

	/**
	 * Unsubscribes customer from newsletter
	 * @param {IUnsubscribeToNewsletterOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {IUnsubscribeToNewsletterResponse} - Status of the request
	 */
	public async unsubscribeFromNewsletter(
		fetchOptions: IUnsubscribeToNewsletterOptions,
		throwOnError?: boolean
	): Promise<IUnsubscribeToNewsletterResponse> {
		const response: IUnsubscribeToNewsletterResponse = await this.callCustomEndpoint<IUnsubscribeToNewsletterResponse>(
			fetchOptions,
			endpointsMap.UNSUBSCRIBE
		);

		if (throwOnError && response.unsubscriptionStatus !== 'UNSUBSCRIBED') {
			throw new Error('Unsubscription failed.');
		}

		return response;
	}

	/**
	 * Unsubscribes customer from newsletter using an opaque uid (DELETE profiles/uid/{c_uid}/subscription).
	 * @param {IUnsubscribeFromNewsletterByUidOptions} fetchOptions - must include pathParameters.c_uid
	 * @param {boolean} throwOnError - throw if the API signals failure
	 * @returns {IUnsubscribeFromNewsletterByUidResponse}
	 */
	public async unsubscribeFromNewsletterByUid(
		fetchOptions: IUnsubscribeFromNewsletterByUidOptions,
		throwOnError?: boolean
	): Promise<IUnsubscribeFromNewsletterByUidResponse> {
		const response: IUnsubscribeFromNewsletterByUidResponse = await this.callCustomEndpoint<
			IUnsubscribeFromNewsletterByUidResponse
		>(fetchOptions, endpointsMap.UNSUBSCRIBE_BY_UID);

		if (throwOnError) {
			const isErrorType = /error/.test(response?.type);
			const isFailedStatus = response?.unsubscriptionStatus != null && response?.unsubscriptionStatus !== 'UNSUBSCRIBED';

			if (isErrorType || isFailedStatus) {

				throw new Error('Unsubscription by uid failed.');
			}
		}

		return response;
	}
}

export default CustomShopperNewsletter;
